// MARK://dhk8/pos/features/SplitDialog
// Split-the-bill sheet, rebuilt for the 534×738 portrait POS. Three modes via a
// pill segmented control:
//   • N partes iguales — pick 2-10 parts, see the per-part amount.
//   • Por porcentaje — per-account % rows that must sum to 100.
//   • Por ítems — every order line gets a sub-account chip picker (Sin asignar /
//     Cuenta A / Cuenta B / …); a live per-account total strip shows the result.
// On confirm we hand a SplitResult back to OrderPage → order-store.splitOrder
// (which creates the sub-accounts and, for ITEMS, moves each line via the
// `assignOrderItemSubAccount` stub). All `--pos-*` tokens — theme-aware.

import { useEffect, useMemo, useState } from 'react'
import { Plus, Trash2, Equal, Percent, ListChecks } from 'lucide-react'
import type { OrderDto } from '../stores/order-store.js'
import { PRINCIPAL_SUB_ID } from '../stores/order-store.js'
import { formatCents } from '../lib/money.js'
import BottomSheet from './pos/BottomSheet.js'

export type SplitMode = 'EVEN' | 'PERCENT' | 'ITEMS'

export interface SplitResult {
  mode: SplitMode
  parts?: number
  labels: string[]
  percents?: number[]
  /** orderItemId → sub-account index in `labels` (ITEMS mode only). */
  itemAssignments?: Record<string, number>
}

export interface SplitDialogProps {
  open: boolean
  onClose: () => void
  order: OrderDto | null
  onSplit: (result: SplitResult) => Promise<void>
}

const defaultLabel = (i: number) => `Cuenta ${String.fromCharCode(65 + i)}`

export default function SplitDialog({ open, onClose, order, onSplit }: SplitDialogProps) {
  const [mode, setMode] = useState<SplitMode>('EVEN')
  const [parts, setParts] = useState(2)
  const [percents, setPercents] = useState<number[]>([50, 50])
  const [labels, setLabels] = useState<string[]>([defaultLabel(0), defaultLabel(1)])
  const [assignments, setAssignments] = useState<Record<string, number>>({})
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Lane #166 — al abrir el dialog, si la orden ya tiene sub-cuentas reales
  // creadas en una sesión anterior, inicializar labels + parts con esos
  // valores (no con los defaults 'Cuenta A' / 'Cuenta B'). Sin esto, los
  // labels que el user EDITA quedan asignados a sub-cuentas NUEVAS que se
  // crean en cada submit — los renames de existentes se perdían.
  useEffect(() => {
    if (!open || !order) return
    const existing = order.subAccounts.filter((s) => s.id !== PRINCIPAL_SUB_ID)
    if (existing.length > 0) {
      const existingLabels = existing.map((s) => s.label)
      setLabels((prev) => {
        // Mantén labels más largas que existing si user ya añadió más cuentas
        const merged = [...existingLabels]
        for (let i = existingLabels.length; i < prev.length; i++) {
          merged.push(prev[i] ?? defaultLabel(i))
        }
        return merged
      })
      setParts(Math.max(2, existing.length))
    }
  }, [open, order])

  const items = order?.items ?? []
  const totalCents = order?.totalCents ?? 0

  function setN(n: number, lo = 2, hi = 10) {
    const bounded = Math.max(lo, Math.min(hi, Number.isFinite(n) ? n : lo))
    setParts(bounded)
    setLabels((prev) => {
      const next = prev.slice()
      while (next.length < bounded) next.push(defaultLabel(next.length))
      while (next.length > bounded) next.pop()
      return next
    })
    setPercents(() => {
      const even = +(100 / bounded).toFixed(2)
      return Array.from({ length: bounded }, () => even)
    })
    // drop assignments that point past the new count
    setAssignments((prev) => {
      const next: Record<string, number> = {}
      for (const [k, v] of Object.entries(prev)) if (v < bounded) next[k] = v
      return next
    })
  }

  const accountTotals = useMemo(() => {
    const totals = Array.from({ length: labels.length }, () => 0)
    for (const it of items) {
      const idx = assignments[it.id]
      if (idx !== undefined && idx < totals.length) totals[idx] = (totals[idx] ?? 0) + it.totalCents
    }
    return totals
  }, [items, assignments, labels.length])

  const unassignedTotal = useMemo(() => {
    return items.filter((it) => assignments[it.id] === undefined).reduce((s, it) => s + it.totalCents, 0)
  }, [items, assignments])

  async function handleSubmit() {
    setBusy(true)
    setError(null)
    try {
      if (mode === 'PERCENT') {
        const sum = percents.reduce((s, p) => s + p, 0)
        if (Math.abs(sum - 100) > 0.05) { setError(`Los porcentajes deben sumar 100% (van ${sum.toFixed(2)}%)`); setBusy(false); return }
      }
      await onSplit({
        mode,
        parts,
        labels,
        ...(mode === 'PERCENT' ? { percents } : {}),
        ...(mode === 'ITEMS' ? { itemAssignments: assignments } : {}),
      })
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo dividir la cuenta')
    } finally {
      setBusy(false)
    }
  }

  function cycleAssignment(itemId: string) {
    setAssignments((prev) => {
      const cur = prev[itemId]
      const next = { ...prev }
      if (cur === undefined) next[itemId] = 0
      else if (cur + 1 >= labels.length) delete next[itemId]
      else next[itemId] = cur + 1
      return next
    })
  }

  if (!open || !order) return null

  return (
    <BottomSheet
      open
      onClose={() => !busy && onClose()}
      title="Dividir cuenta"
      heightFraction={0.86}
      footer={
        <div className="dhk8-spl__foot">
          <button type="button" className="dhk8-spl__btn dhk8-spl__btn--ghost" onClick={onClose} disabled={busy}>Cancelar</button>
          <button type="button" className="dhk8-spl__btn dhk8-spl__btn--primary" onClick={() => void handleSubmit()} disabled={busy}>
            {busy ? 'Dividiendo…' : 'Dividir cuenta'}
          </button>
        </div>
      }
    >
      <div className="dhk8-spl">
        <div className="dhk8-spl__seg" role="tablist" aria-label="Modo de división">
          {([['EVEN', 'Partes iguales', Equal], ['PERCENT', 'Porcentaje', Percent], ['ITEMS', 'Por ítems', ListChecks]] as const).map(([m, lbl, Icon]) => (
            <button key={m} type="button" role="tab" aria-selected={mode === m}
              className={`dhk8-spl__seg-btn${mode === m ? ' dhk8-spl__seg-btn--on' : ''}`} onClick={() => setMode(m)}>
              <Icon size={14} /> {lbl}
            </button>
          ))}
        </div>

        {(mode === 'EVEN' || mode === 'PERCENT') && (
          <div className="dhk8-spl__field">
            <span className="dhk8-spl__label">Número de cuentas</span>
            <div className="dhk8-spl__stepper">
              <button type="button" className="dhk8-spl__step" aria-label="Menos cuentas" disabled={parts <= 2} onClick={() => setN(parts - 1)}>−</button>
              <span className="dhk8-spl__step-val">{parts}</span>
              <button type="button" className="dhk8-spl__step" aria-label="Más cuentas" disabled={parts >= 10} onClick={() => setN(parts + 1)}>+</button>
            </div>
          </div>
        )}

        {mode === 'EVEN' && (
          <div className="dhk8-spl__note">
            Cada cuenta = <strong>{formatCents(Math.floor(totalCents / parts))}</strong>
            {totalCents % parts !== 0 && <span className="dhk8-spl__note-rem"> · la última absorbe {formatCents(totalCents % parts)}</span>}
          </div>
        )}

        {mode === 'PERCENT' && (
          <div className="dhk8-spl__pct">
            {percents.map((p, i) => (
              <div key={i} className="dhk8-spl__pct-row">
                <span className="dhk8-spl__pct-name">{labels[i]}</span>
                <input type="number" min={0} max={100} step={0.5} className="dhk8-spl__pct-input"
                  value={String(p)}
                  onChange={(e) => {
                    const n = Number.parseFloat(e.currentTarget.value)
                    setPercents((prev) => { const next = prev.slice(); next[i] = Number.isFinite(n) ? n : 0; return next })
                  }} />
                <span className="dhk8-spl__pct-amt">{formatCents(Math.round((totalCents * p) / 100))}</span>
              </div>
            ))}
            <div className={`dhk8-spl__pct-sum${Math.abs(percents.reduce((s, p) => s + p, 0) - 100) > 0.05 ? ' dhk8-spl__pct-sum--bad' : ''}`}>
              Suma {percents.reduce((s, p) => s + p, 0).toFixed(2)}% / 100%
            </div>
          </div>
        )}

        {mode === 'ITEMS' && (
          <>
            <div className="dhk8-spl__field">
              <span className="dhk8-spl__label">Número de cuentas</span>
              <div className="dhk8-spl__stepper">
                <button type="button" className="dhk8-spl__step" aria-label="Menos cuentas" disabled={labels.length <= 2} onClick={() => setN(labels.length - 1)}>−</button>
                <span className="dhk8-spl__step-val">{labels.length}</span>
                <button type="button" className="dhk8-spl__step" aria-label="Más cuentas" disabled={labels.length >= 10} onClick={() => setN(labels.length + 1)}>+</button>
              </div>
            </div>
            <div className="dhk8-spl__hint">Toca el botón de cada ítem para moverlo entre cuentas. Lo que quede sin asignar permanece en la cuenta principal.</div>
            <ul className="dhk8-spl__items">
              {items.length === 0 && <li className="dhk8-spl__items-empty">La orden no tiene ítems.</li>}
              {items.map((it) => {
                const idx = assignments[it.id]
                const label = idx === undefined ? 'Sin asignar' : labels[idx]
                return (
                  <li key={it.id} className="dhk8-spl__item">
                    <div className="dhk8-spl__item-text">
                      <span className="dhk8-spl__item-name">×{it.quantity} {it.name}</span>
                      <span className="dhk8-spl__item-amt">{formatCents(it.totalCents)}</span>
                    </div>
                    <button type="button" className={`dhk8-spl__assign${idx !== undefined ? ' dhk8-spl__assign--on' : ''}`} onClick={() => cycleAssignment(it.id)}>
                      {label}
                    </button>
                  </li>
                )
              })}
            </ul>
            <div className="dhk8-spl__totals">
              <div className="dhk8-spl__totals-row"><span>Sin asignar</span><span>{formatCents(unassignedTotal)}</span></div>
              {accountTotals.map((t, i) => (
                <div key={i} className="dhk8-spl__totals-row dhk8-spl__totals-row--acct"><span>{labels[i]}</span><span>{formatCents(t)}</span></div>
              ))}
            </div>
          </>
        )}

        {(mode === 'EVEN' || mode === 'PERCENT' || mode === 'ITEMS') && (
          <div className="dhk8-spl__field">
            <span className="dhk8-spl__label">Nombres de las cuentas</span>
            {labels.map((lb, i) => (
              <div key={i} className="dhk8-spl__name-row">
                <input type="text" className="dhk8-spl__name-input" value={lb}
                  onChange={(e) => setLabels((prev) => { const next = prev.slice(); next[i] = e.currentTarget.value; return next })} />
                <button type="button" className="dhk8-spl__name-del" aria-label="Quitar cuenta" disabled={labels.length <= 2}
                  onClick={() => { if (labels.length <= 2) return; setN(labels.length - 1) }}>
                  <Trash2 size={15} />
                </button>
              </div>
            ))}
            <button type="button" className="dhk8-spl__name-add" disabled={labels.length >= 10} onClick={() => setN(labels.length + 1)}>
              <Plus size={15} /> Añadir cuenta
            </button>
          </div>
        )}

        {error && <div className="dhk8-spl__alert">{error}</div>}
      </div>
      <style>{styles}</style>
    </BottomSheet>
  )
}

const styles = `
  .dhk8-spl { display:flex; flex-direction:column; gap:var(--pos-space-4,16px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-spl__field { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); }
  .dhk8-spl__label { font-size:var(--pos-text-xxs,11px); letter-spacing:.08em; text-transform:uppercase; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__seg { display:grid; grid-template-columns:1fr 1fr 1fr; gap:var(--pos-space-1,4px); padding:3px; background:var(--pos-canvas,#0D110F); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); }
  .dhk8-spl__seg-btn { display:inline-flex; align-items:center; justify-content:center; gap:4px; min-height:38px; border:none; border-radius:var(--pos-radius-sm,6px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font:inherit; font-size:var(--pos-text-xxs,11px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-spl__seg-btn--on { background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-spl__stepper { display:flex; align-items:center; gap:var(--pos-space-3,12px); align-self:flex-start; }
  .dhk8-spl__step { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-xl,20px); line-height:1; cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-spl__step:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-spl__step:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-spl__step-val { min-width:40px; text-align:center; font-size:var(--pos-text-xl,20px); font-weight:var(--pos-weight-semibold,600); font-variant-numeric:tabular-nums; }
  .dhk8-spl__note { font-size:var(--pos-text-sm,13px); color:var(--pos-fg-secondary,#B6BFB8); }
  .dhk8-spl__note strong { color:var(--pos-fg,#E8EFE9); font-variant-numeric:tabular-nums; }
  .dhk8-spl__note-rem { color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__pct { display:flex; flex-direction:column; gap:var(--pos-space-2,8px); padding:var(--pos-space-3,12px); background:var(--pos-canvas,#0D110F); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); }
  .dhk8-spl__pct-row { display:grid; grid-template-columns:1fr 88px 80px; gap:var(--pos-space-2,8px); align-items:center; }
  .dhk8-spl__pct-name { font-size:var(--pos-text-sm,13px); color:var(--pos-fg-secondary,#B6BFB8); white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
  .dhk8-spl__pct-input { min-height:40px; padding:0 var(--pos-space-2,8px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-sm,6px); background:var(--pos-surface,#161C18); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-md,15px); font-variant-numeric:tabular-nums; text-align:right; outline:none; }
  .dhk8-spl__pct-input:focus { border-color:var(--pos-tint,#1F8B3A); }
  .dhk8-spl__pct-amt { text-align:right; font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xs,12px); font-variant-numeric:tabular-nums; color:var(--pos-fg,#E8EFE9); }
  .dhk8-spl__pct-sum { font-size:var(--pos-text-xxs,11px); color:var(--pos-fg-tertiary,#7E8C82); text-align:right; font-variant-numeric:tabular-nums; }
  .dhk8-spl__pct-sum--bad { color:var(--pos-danger,#E5484D); }
  .dhk8-spl__hint { font-size:var(--pos-text-xxs,11px); line-height:1.35; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__items { list-style:none; margin:0; padding:0; border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); overflow:hidden; }
  .dhk8-spl__items-empty { padding:var(--pos-space-4,16px); text-align:center; font-size:var(--pos-text-xs,12px); color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__item { display:flex; align-items:center; gap:var(--pos-space-3,12px); padding:var(--pos-space-2,8px) var(--pos-space-3,12px); border-bottom:1px solid var(--pos-separator,rgba(255,255,255,.07)); }
  .dhk8-spl__item:last-child { border-bottom:none; }
  .dhk8-spl__item-text { flex:1; min-width:0; display:flex; flex-direction:column; gap:1px; }
  .dhk8-spl__item-name { font-size:var(--pos-text-sm,13px); color:var(--pos-fg,#E8EFE9); line-height:1.25; }
  .dhk8-spl__item-amt { font-family:var(--font-mono,ui-monospace); font-size:var(--pos-text-xxs,11px); font-variant-numeric:tabular-nums; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__assign { flex-shrink:0; min-height:38px; padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-pill,999px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font:inherit; font-size:var(--pos-text-xs,12px); font-weight:var(--pos-weight-medium,500); cursor:pointer; white-space:nowrap; transition:all var(--pos-duration-fast,120ms) ease; }
  .dhk8-spl__assign:hover { background:var(--pos-elevated,#1E2520); }
  .dhk8-spl__assign--on { background:var(--pos-tint,#1F8B3A); border-color:var(--pos-tint,#1F8B3A); color:#fff; }
  .dhk8-spl__totals { display:flex; flex-direction:column; gap:2px; padding:var(--pos-space-3,12px); background:var(--pos-elevated,#1E2520); border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); }
  .dhk8-spl__totals-row { display:flex; justify-content:space-between; font-size:var(--pos-text-xs,12px); font-variant-numeric:tabular-nums; color:var(--pos-fg-tertiary,#7E8C82); }
  .dhk8-spl__totals-row--acct { color:var(--pos-fg,#E8EFE9); }
  .dhk8-spl__name-row { display:flex; gap:var(--pos-space-2,8px); }
  .dhk8-spl__name-input { flex:1; min-height:var(--pos-touch-target,44px); padding:0 var(--pos-space-3,12px); border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:var(--pos-canvas,#0D110F); color:var(--pos-fg,#E8EFE9); font:inherit; font-size:var(--pos-text-md,15px); outline:none; }
  .dhk8-spl__name-input:focus { border-color:var(--pos-tint,#1F8B3A); box-shadow:0 0 0 3px var(--pos-tint-soft,rgba(31,139,58,.12)); }
  .dhk8-spl__name-del { width:44px; height:44px; flex-shrink:0; display:flex; align-items:center; justify-content:center; border:1px solid var(--pos-separator,rgba(255,255,255,.07)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); cursor:pointer; }
  .dhk8-spl__name-del:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-spl__name-del:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-spl__name-add { display:inline-flex; align-items:center; gap:6px; align-self:flex-start; min-height:38px; padding:0 var(--pos-space-3,12px); border:1px dashed var(--pos-separator-strong,rgba(255,255,255,.14)); border-radius:var(--pos-radius-md,8px); background:transparent; color:var(--pos-fg-secondary,#B6BFB8); font:inherit; font-size:var(--pos-text-xs,12px); cursor:pointer; }
  .dhk8-spl__name-add:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); color:var(--pos-fg,#E8EFE9); }
  .dhk8-spl__name-add:disabled { opacity:.4; cursor:not-allowed; }
  .dhk8-spl__alert { color:var(--pos-danger,#E5484D); background:color-mix(in oklch, var(--pos-danger,#E5484D) 14%, transparent); border:1px solid color-mix(in oklch, var(--pos-danger,#E5484D) 40%, transparent); border-radius:var(--pos-radius-md,8px); padding:var(--pos-space-3,12px); font-size:var(--pos-text-sm,13px); }
  .dhk8-spl__foot { display:flex; gap:var(--pos-space-3,12px); padding:var(--pos-space-3,12px) var(--pos-space-4,16px) var(--pos-space-4,16px); }
  .dhk8-spl__btn { flex:1; min-height:var(--pos-touch-target,44px); display:inline-flex; align-items:center; justify-content:center; border-radius:var(--pos-radius-md,8px); font:inherit; font-size:var(--pos-text-md,15px); font-weight:var(--pos-weight-medium,500); cursor:pointer; transition:background var(--pos-duration-fast,120ms) ease; }
  .dhk8-spl__btn:disabled { opacity:.5; cursor:not-allowed; }
  .dhk8-spl__btn--ghost { border:1px solid var(--pos-separator-strong,rgba(255,255,255,.14)); background:transparent; color:var(--pos-fg,#E8EFE9); }
  .dhk8-spl__btn--ghost:hover:not(:disabled) { background:var(--pos-elevated,#1E2520); }
  .dhk8-spl__btn--primary { border:1px solid var(--pos-tint,#1F8B3A); background:var(--pos-tint,#1F8B3A); color:#fff; font-weight:var(--pos-weight-semibold,600); box-shadow:var(--pos-shadow-1,0 1px 2px rgba(0,0,0,.18)); }
  .dhk8-spl__btn--primary:hover:not(:disabled) { filter:brightness(1.06); }
`
